import Heading from '@atlaskit/heading';
import { Flex, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { AdControls } from '@post-office/components--ad-controls';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { defaultMessageBackend } from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

// assertMessageTemplateContext is a validation file created by Post Office's validation library Vindicated. It will be included when you create your message template. You can find out more about vindicated here: https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/vindicated-validation-library/#vindicated:-post-office's-runtime-type-validation-library
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import AINotesIcon from '../../../assets/ai-notes-icon.svg';
import { useLoomAiMeetingNotesCrossFlow } from '../../../hooks/use-loom-ai-meeting-notes-cross-flow';

// Currently we won't look into the message backend, but this is where you can add stages which
// change your message before it gets sent to the frontend.
export const MessageBackend = PlacementBackend.createMessageBackend(defaultMessageBackend);

const whyAmISeeingThisModalContent = {
	title: (
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-title"
			description="Title of the reasons modal explaining why the loom cross-flow touchpoint is being shown"
			defaultMessage="Why am I seeing this?"
		/>
	),
	message: (
		<p>
			<FormattedMessage
				id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-explanation"
				description="Explanation as to why the loom cross-flow touchpoint is being shown"
				defaultMessage="We think that Loom will improve your team's productivity. The recommendation is based on:"
			/>
		</p>
	),
	reasons: [
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-one"
			description="First bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="the size of your team"
		/>,
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-two"
			description="Second bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="your existing Atlassian products"
		/>,
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-three"
			description="Third bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="usage of a meeting notes template in Confluence"
		/>,
	],
};

// This is the Message Frontend. In this Placement we have render.Component which takes a React Component which will render your message in the Placement.
export const MessageFrontend = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: () => {
			const intl = useIntl();

			const title = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.header',
				defaultMessage: 'Automate meeting notes',
				description: 'Header text for loom ai meeting notes in confluence banner',
			});

			const description = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.body',
				defaultMessage:
					'Capture meetings with Loom and get instant notes and action items from AI.',
				description: 'Body (description) text for loom ai meeting notes in confluence banner',
			});

			const ctaText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.cta',
				defaultMessage: 'Learn More',
				description: 'Call to action text for loom ai meeting notes in confluence banner',
			});

			const imageAltText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.image-alt',
				defaultMessage: 'Notepad with sparkling background',
				description: 'Alternate text for picture of notepad with sparkles behind it',
			});

			const { messageInstanceClicked } = useMessageInstanceEvent();
			const { openCrossFlow, isLoading } = useLoomAiMeetingNotesCrossFlow();
			const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

			useEffect(() => {
				fireViewedUIEvent(
					{
						action: 'viewed',
						actionSubject: 'card',
						actionSubjectId: 'confluenceLoomSideNavBanner',
					},
					{
						source: 'confluenceLoomSideNavBannerCard',
						recommendedProductIds: ['loom'],
					},
				);
			}, [fireViewedUIEvent]);

			/**
			 * Handler for when user dismisses via ad-controls (Not interested)
			 */
			const handleNotInterested = useCallback(() => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'dismiss',
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend: {},
				});
			}, [messageInstanceClicked]);

			/**
			 * Handler for when user clicks on the CTA.
			 */
			const handleCtaClicked = useCallback(() => {
				openCrossFlow();
				fireInteractedUIEvent(
					{
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'confluenceLoomSideNavBannerLearnMore',
					},
					{ source: 'confluenceLoomSideNavBannerCard', targetProductId: 'loom' },
				);

				/**
				 * Dismiss the message once the user has clicked on it
				 */
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'dismiss',
						clickedElement: 'button',
						buttonType: 'primary',
					},
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend: {},
				});
			}, [messageInstanceClicked, fireInteractedUIEvent, openCrossFlow]);

			return (
				<Flex
					direction="column"
					xcss={xcss({
						borderRadius: 'radius.large',
						backgroundColor: 'color.background.accent.blue.subtlest',
						padding: 'space.150',
						boxShadow: 'elevation.shadow.raised',
					})}
				>
					<Inline
						xcss={xcss({
							justifyContent: 'space-between',
						})}
					>
						<img src={AINotesIcon} alt={imageAltText} role="presentation" width={56} height={56} />
						<AdControls
							showWhyAmISeeingThis
							showRemindMeLater
							whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
							onDismissed={handleNotInterested}
						/>
					</Inline>
					<Stack alignBlock="center" space="space.150">
						<Stack space="space.100">
							<Heading size="small">{title}</Heading>
							<Text>{description}</Text>
						</Stack>
						<Pressable
							isDisabled={isLoading}
							onClick={handleCtaClicked}
							/* Outline buttons do not exist at time of implementation so have to make custom element */
							xcss={xcss({
								backgroundColor: 'color.background.neutral.subtle',
								border: '1px solid',
								borderColor: 'color.border',
								/* Does not exist as token (closest is 3px) */
								borderRadius: '4px',
								minHeight: 'space.300',
								width: 'fit-content',
								paddingInline: 'space.150',
							})}
						>
							<Text size="medium" weight="medium">
								{ctaText}
							</Text>
						</Pressable>
					</Stack>
				</Flex>
			);
		},
	},
});
